import React from 'react';
import { useToaster, toast, type Toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import './CenterToaster.css';

/**
 * App-wide notification renderer — ElectEmb Design System "Toast Messages", Standard pattern.
 *
 * ONE renderer for every toast in the application: all react-hot-toast calls
 * (toast.success/error/loading/… and the notify.* helpers below) render here as top-right
 * stacked cards — white card, status icon chip, title + optional description, close button,
 * and an auto-dismiss progress bar. Slide-in from the right, slide-out on dismiss, hover
 * pauses both the timer and the bar. Non-blocking (the old centre modal + backdrop is gone).
 *
 * Message convention: a plain string is the title; a string containing a newline renders the
 * first line as the title and the rest as the description. notify.* also accepts an explicit
 * description argument.
 *
 * Timing (per the design spec): success 4s · error 6s · warning/info 5s · loading until resolved.
 */

type NotifKind = 'success' | 'error' | 'warning' | 'info' | 'loading';

const DURATION: Record<Exclude<NotifKind, 'loading'>, number> = {
  success: 4000, error: 6000, warning: 5000, info: 5000,
};

const ICON_PATHS: Record<Exclude<NotifKind, 'loading'>, React.ReactNode> = {
  success: <path d="M20 6L9 17l-5-5" />,
  error:   <path d="M18 6L6 18M6 6l12 12" />,
  warning: <>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4M12 17h.01" />
  </>,
  info: <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-5M12 8h.01" />
  </>,
};

const CHIP_CLASS: Record<NotifKind, string> = {
  success: 'ok', error: 'bad', warning: 'warn', info: 'info', loading: 'load',
};

function kindOf(t: Toast): NotifKind {
  if (t.type === 'success') return 'success';
  if (t.type === 'error') return 'error';
  if (t.type === 'loading') return 'loading';
  const cls = (t.className as string) ?? '';
  if (cls.includes('notif-warning')) return 'warning';
  if (cls.includes('notif-success')) return 'success';
  if (cls.includes('notif-error')) return 'error';
  return 'info';
}

/** First line = title, remainder = description. */
function splitMessage(msg: React.ReactNode): { title: React.ReactNode; desc: string | null } {
  if (typeof msg !== 'string') return { title: msg, desc: null };
  const nl = msg.indexOf('\n');
  if (nl < 0) return { title: msg, desc: null };
  return { title: msg.slice(0, nl), desc: msg.slice(nl + 1).trim() || null };
}

const MAX_VISIBLE = 5;

export function CenterToaster() {
  const { toasts, handlers } = useToaster({
    duration: DURATION.info,
    success: { duration: DURATION.success },
    error:   { duration: DURATION.error },
  });

  const visible = toasts.slice(0, MAX_VISIBLE);
  if (visible.length === 0) return null;

  return (
    <div className="et-stack" aria-live="polite"
         onMouseEnter={handlers.startPause} onMouseLeave={handlers.endPause}>
      {visible.map(t => {
        const kind = kindOf(t);
        const message = typeof t.message === 'function' ? t.message(t) : t.message;
        const { title, desc } = splitMessage(message as React.ReactNode);
        const ttl = t.duration ?? DURATION.info;
        return (
          <div
            key={t.id}
            role={kind === 'error' ? 'alert' : 'status'}
            className={`et-toast ${CHIP_CLASS[kind]}${t.visible ? '' : ' et-leaving'}`}
          >
            <div className={`et-ticon ${CHIP_CLASS[kind]}`}>
              {kind === 'loading' ? (
                <div className="et-spin" />
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                     strokeWidth={kind === 'warning' || kind === 'info' ? 2.2 : 2.4}
                     strokeLinecap="round" strokeLinejoin="round">
                  {ICON_PATHS[kind]}
                </svg>
              )}
            </div>
            <div className="et-tbody">
              <div className="et-ttitle">{title}</div>
              {desc && <div className="et-tdesc">{desc}</div>}
            </div>
            {kind !== 'loading' && (
              <button className="et-tclose" aria-label="Close notification" onClick={() => toast.dismiss(t.id)}>
                <X size={13} />
              </button>
            )}
            {kind !== 'loading' && Number.isFinite(ttl) && t.visible && (
              <div className="et-tprog" style={{ animationDuration: `${ttl}ms` }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Typed helpers — same API as before, plus an optional description line. */
export const notify = {
  success: (msg: string, desc?: string) => toast.success(desc ? `${msg}\n${desc}` : msg),
  error:   (msg: string, desc?: string) => toast.error(desc ? `${msg}\n${desc}` : msg),
  warning: (msg: string, desc?: string) => toast(desc ? `${msg}\n${desc}` : msg, { className: 'notif-warning', duration: DURATION.warning }),
  info:    (msg: string, desc?: string) => toast(desc ? `${msg}\n${desc}` : msg, { className: 'notif-info', duration: DURATION.info }),
};
